import React from 'react';
import Modal from '../Modal';
import { Camion } from '../../types';
import { TruckIcon, CalendarIcon } from '@heroicons/react/24/outline';

interface VehicleDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Camion;
}


const VehicleDetailsModal: React.FC<VehicleDetailsModalProps> = ({ isOpen, onClose, vehicle }) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Non disponible';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Détails du véhicule" size="lg">
      <div className="space-y-6">
        {/* En-tête avec icône */}
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <TruckIcon className="w-8 h-8 text-orange-600" />
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {vehicle.immatriculation}
            </h2>
            <p className="text-lg text-gray-600">
              {vehicle.marque} {vehicle.modele}
            </p>
          </div>
        </div>

        {/* Informations principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Informations générales
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Immatriculation:</span>
                <span className="text-gray-900 font-mono">{vehicle.immatriculation}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Marque:</span>
                <span className="text-gray-900">{vehicle.marque}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Modèle:</span>
                <span className="text-gray-900">{vehicle.modele}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Capacité:</span>
                <span className="text-gray-900">{vehicle.capacite} kg</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Propriétaire:</span>
                <span className="text-gray-900">{vehicle.proprietaire}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Statistiques et métriques
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Nombre de passages:</span>
                <span className="text-gray-900">{vehicle.nombre_passages}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Tare théorique:</span>
                <span className="text-gray-900">
                  {vehicle.tare_theorique ? `${vehicle.tare_theorique} kg` : 'Non définie'}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Poids tare moyen:</span>
                <span className="text-gray-900">
                  {vehicle.poids_tare_moyen ? `${vehicle.poids_tare_moyen} kg` : 'Non disponible'}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Dernière pesée:</span>
                <span className="text-gray-900">
                  {formatDate(vehicle.derniere_pesee)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Informations de dates */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <CalendarIcon className="w-5 h-5 mr-2 text-gray-600" />
            Informations temporelles
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Créé le:</span>
              <p className="text-gray-900">{formatDate(vehicle.created_at)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Modifié le:</span>
              <p className="text-gray-900">{formatDate(vehicle.updated_at)}</p>
            </div>
          </div>
        </div>

        {/* Bouton de fermeture */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default VehicleDetailsModal;
